'use client'
import React, { ReactNode } from 'react'

import styles from './Button.module.css'
export type SharedButtonProps = {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'quinary' | 'senary' | 'darkest'
  size?: 'xlarge' | 'large' | 'default' | 'small'
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  onClick?: () => void
  label: ReactNode
  disabled?: boolean
  css?: React.CSSProperties
  openInNewWindow?: boolean | string
}

type TypeButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  SharedButtonProps & {
    type: 'button' | 'submit'
    onClick?: () => void
  }

type TypeButtonLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  SharedButtonProps & {
    type: 'link'
  }

type ButtonProps = TypeButtonProps | TypeButtonLinkProps

export const PhnxButton: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'default',
  type,
  icon,
  iconPosition = 'left',
  onClick,
  label,
  disabled = false,
  css,
  openInNewWindow,
  className,
  ...props
}) => {
  let ariaLabel: string | null = null
  let iconOnly = false

  // check if only icon and no children
  if (icon && !label) {
    iconOnly = true
  }

  let checkWindow = false

  if (type === 'link') {
    checkWindow = openInNewWindow === true || openInNewWindow === 'true'
  }

  // set the button class based on the variant, variantType, and size props
  let btnClass = `${styles['button']} ${
    styles[`variant-${variant}`]
  } ${styles[`size-${size}`]} ${iconOnly ? (styles['icon-only'] ?? '') : ''} ${className ?? ''}`

  // expand the button class for GTAG triggers
  btnClass += ` btn ${variant === 'primary' && 'solid orange'}`

  // if the button is disabled, add the disabled class
  if (disabled) {
    btnClass += ` ${styles['disabled']}`
  }

  const handleClick = () => {
    onClick && onClick()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (event.key === 'Enter') {
      onClick && onClick()
    }
  }

  let buttonInner = (
    <>
      {icon && iconPosition === 'left' && (
        <span className={`${styles['icon']} ${styles['left']} `}>
          <span className={`fx-icon ${icon}`}></span>
        </span>
      )}
      <span className={`button__text btn ${variant === 'primary' && 'solid orange'}`}>{label}</span>
      {icon && iconPosition === 'right' && (
        <span className={`fx-icon ${styles['icon']} ${styles['right']} `}>
          <span className={`fx-icon ${icon}`}></span>
        </span>
      )}
    </>
  )

  /* The code block is checking if the `type` prop of the `Button` component is set to "link". If it is,
it creates an `<a>` element instead of a `<button>` element. */
  if (type === 'link') {
    // cast props to linkProps to avoid type errors
    const linkProps = props as TypeButtonLinkProps

    return (
      <a
        href={linkProps.href ? linkProps.href : '#'}
        className={btnClass}
        aria-label={ariaLabel ? ariaLabel : ''}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-testid="link"
        target={checkWindow ? '_blank' : '_self'}
        style={{
          ...css,
        }}
        {...linkProps}
      >
        {buttonInner}
      </a>
    )
  }

  // default to button tag
  const submitProps = props as TypeButtonProps
  return (
    <button
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel ? ariaLabel : ''}
      className={btnClass}
      disabled={disabled}
      {...submitProps}
      type={type}
      data-test-variant={variant}
      style={{
        ...css,
      }}
    >
      {buttonInner}
    </button>
  )
}
